import { db } from "../firebase";
import {
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
} from "firebase/firestore";
import { langNgheFirestore } from "../lib/langNgheFirestore";

const banNhapCol = () => collection(db, "banNhapThuMua");

// 1 bản nháp cho mỗi (chủ vườn, vườn) — dùng 2 mã ghép làm ID để "Lưu nháp"
// nhiều lần là ghi đè tự nhiên (setDoc), không tạo trùng. Trước đây khoá còn
// ghép thêm maSot (khi FormThuMua bắt buộc quét đúng 1 Sọt TRƯỚC khi vào
// form) — nay việc quét Sọt xảy ra NGAY TRONG form, theo từng khối loại bơ
// (xem FormThuMua/nhomLoaiBo), không còn 1 Sọt "cố định" đại diện cho cả
// phiếu nữa nên bỏ khỏi khoá. Nháp đã lưu TRƯỚC khi đổi khoá này sẽ "mồ côi"
// (không tra ra được qua khoá mới — dữ liệu Firestore không mất, chỉ không
// ai trỏ tới) — chấp nhận đánh đổi này vì nháp chỉ là dữ liệu tạm, không có
// script di chuyển.
const idTaiLieu = (maChuVuon, maVuon) => `${maChuVuon}_${maVuon}`;

async function ganTenLienQuan(dsNhap) {
  const maChuVuonList = [...new Set(dsNhap.map((n) => n.maChuVuon).filter(Boolean))];
  const maVuonList = [...new Set(dsNhap.map((n) => n.maVuon).filter(Boolean))];
  // Mỗi dòng loại bơ trong nháp có thể gắn 1 Sọt khác nhau (nhiều khối loại
  // bơ, mỗi khối có thể trải nhiều Sọt nếu 1 Sọt đầy giữa chừng) — gom mã Sọt
  // từ MỌI item, không còn đúng 1 field maSot cố định ở gốc tài liệu nháp
  // như trước khi đổi cấu trúc.
  const maSotList = [
    ...new Set(dsNhap.flatMap((n) => (n.items ?? []).map((it) => it.maSot)).filter(Boolean)),
  ];

  const [chuVuonDocs, vuonDocs, sotDocs] = await Promise.all([
    Promise.all(maChuVuonList.map((id) => getDoc(doc(db, "chuVuon", id)))),
    Promise.all(maVuonList.map((id) => getDoc(doc(db, "vuon", id)))),
    Promise.all(maSotList.map((id) => getDoc(doc(db, "sot", id)))),
  ]);

  const tenChuVuon = Object.fromEntries(chuVuonDocs.map((d) => [d.id, d.data()?.hoTen]));
  const tenVuon = Object.fromEntries(vuonDocs.map((d) => [d.id, d.data()?.tenVuon]));
  const maSotHienThi = Object.fromEntries(sotDocs.map((d) => [d.id, d.data()?.maSot]));

  return dsNhap.map((n) => {
    // Mọi mã Sọt phân biệt nháp này đã chạm tới (bỏ qua dòng chưa quét Sọt,
    // vẫn đang dở dang) — dùng để hiện "LV1HS6 + N Sọt khác" giống cách
    // XacNhanThanhToan/GiaoDichCuaToi hiện PO trải nhiều Sọt, thay vì chỉ có
    // đúng 1 mã cố định như thiết kế cũ.
    const dsMaSotNhap = [...new Set((n.items ?? []).map((it) => it.maSot).filter(Boolean))];
    return {
      ...n,
      tenChuVuon: tenChuVuon[n.maChuVuon] ?? null,
      tenVuon: tenVuon[n.maVuon] ?? null,
      maSotHienThi: dsMaSotNhap[0] ? maSotHienThi[dsMaSotNhap[0]] ?? null : null,
      soSotKhac: Math.max(0, dsMaSotNhap.length - 1),
    };
  });
}

export const banNhapThuMuaApi = {
  async lay(maChuVuon, maVuon) {
    if (!maChuVuon || !maVuon) return null;
    const snap = await getDoc(doc(db, "banNhapThuMua", idTaiLieu(maChuVuon, maVuon)));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  // Danh sách nháp của 1 nhân viên — hiện ở "Giao dịch của tôi" để tiếp tục
  // hoặc xoá mà không cần quét lại Sọt nào.
  async cuaToi(uid) {
    if (!uid) return [];
    const snap = await getDocs(query(banNhapCol(), where("nguoiTao", "==", uid)));
    const ketQua = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const ganTen = await ganTenLienQuan(ketQua);
    return ganTen.sort((a, b) => (a.capNhatLuc < b.capNhatLuc ? 1 : -1));
  },

  langNgheCuaToi(uid, onChange, onError) {
    if (!uid) {
      onChange([]);
      return () => {};
    }
    return langNgheFirestore(query(banNhapCol(), where("nguoiTao", "==", uid)), {
      mapAsync: async (raw) => {
        const ganTen = await ganTenLienQuan(raw ?? []);
        return ganTen.sort((a, b) => (a.capNhatLuc < b.capNhatLuc ? 1 : -1));
      },
      onChange,
      onError,
    });
  },

  async luu({ maChuVuon, maVuon, items, hinhThucTt, ghiChu, maHopTacXa, nguoiTao }) {
    const ref = doc(db, "banNhapThuMua", idTaiLieu(maChuVuon, maVuon));
    const data = {
      maChuVuon,
      maVuon,
      items,
      hinhThucTt: hinhThucTt ?? null,
      ghiChu: ghiChu ?? null,
      maHopTacXa: maHopTacXa || null,
      nguoiTao: nguoiTao ?? null,
      capNhatLuc: new Date().toISOString(),
    };
    await setDoc(ref, data);
    return { id: ref.id, ...data };
  },

  async xoa(maChuVuon, maVuon) {
    await deleteDoc(doc(db, "banNhapThuMua", idTaiLieu(maChuVuon, maVuon)));
  },
};
